import { createPromiseClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { WindsurfBrowserService } from './generated/exa/browser_preview_pb/browser_preview_connect';
import { RegisterPageRequest, CaptureAllTextRequest, CaptureScreenshotRequest, CaptureElementScreenshotRequest, CaptureDOMElementRequest, CaptureConsoleOutputRequest, CaptureElementInnerTextRequest, CaptureCodeBlockRequest, CaptureSelectedTextRequest, CaptureLogBlockRequest, CaptureClickEventRequest, CaptureScrollEventRequest, RecordBrowserAnalyticsRequest, } from './generated/exa/browser_preview_pb/browser_preview_pb';
import { Timestamp } from '@bufbuild/protobuf';
import { isInjectionScriptMessage, isUtilityMessage, isPageRegistrationMessage, isExtensionSignal, isCspViolationMessage, isScriptInjectionFailedMessage, } from './types';
// State management
let credentials = null;
let rpcClient = null;
let isInitialized = false;
// Promise-based initialization tracking
let initializationPromise = null;
let resolveInitialization = null;
// MIME type tracking for tabs
const tabToMimeType = new Map();
// Helper function to extract header value from response headers
function getHeaderFromHeaders(headers, headerName) {
    return headers.find((header) => header.name.toLowerCase() === headerName.toLowerCase());
}
// Track MIME types for tabs using webRequest API
chrome.webRequest.onHeadersReceived.addListener((details) => {
    // has to return undefined as the type definition requires BlockingResponse | undefined
    if (details.tabId !== -1) {
        const contentTypeHeader = getHeaderFromHeaders(details.responseHeaders || [], 'content-type');
        if (contentTypeHeader && contentTypeHeader.value) {
            // Extract the MIME type (before any semicolon)
            const mimeType = contentTypeHeader.value.split(';')[0].trim();
            tabToMimeType.set(details.tabId, mimeType);
        }
    }
    return undefined;
}, { urls: ['*://*/*'], types: ['main_frame'] }, ['responseHeaders']);
// Clean up MIME type tracking when tabs are closed
chrome.tabs.onRemoved.addListener((tabId) => {
    tabToMimeType.delete(tabId);
});
// Initialize RPC client with credentials
function initializeRpcClient() {
    if (!credentials) {
        throw new Error('Cannot initialize RPC client: no credentials');
    }
    const { csrfToken, serverAddress } = credentials;
    // Ensure server address has protocol
    if (!serverAddress.startsWith('http://') && !serverAddress.startsWith('https://')) {
        throw new Error(`Server address must include protocol (http:// or https://): ${serverAddress}`);
    }
    // Create authentication interceptor
    const authInterceptor = (next) => async (req) => {
        req.header.set('x-codeium-csrf-token', csrfToken);
        req.header.set('Origin', serverAddress);
        req.header.set('Referer', serverAddress + '/');
        return next(req);
    };
    // Create transport with interceptor
    const transport = createConnectTransport({
        baseUrl: serverAddress,
        useBinaryFormat: true,
        interceptors: [authInterceptor],
    });
    // Create RPC client
    rpcClient = createPromiseClient(WindsurfBrowserService, transport);
    isInitialized = true;
    // Resolve the initialization promise if it exists
    if (resolveInitialization) {
        resolveInitialization();
        resolveInitialization = null;
    }
}
// Get a promise that resolves when initialization is complete
function waitForInitialization() {
    if (isInitialized) {
        return Promise.resolve();
    }
    if (!initializationPromise) {
        initializationPromise = new Promise((resolve) => {
            resolveInitialization = resolve;
        });
    }
    return initializationPromise;
}
// Global function for Playwright to set credentials
const setCredentialsFunc = async (csrfToken, serverAddress) => {
    // Store credentials in memory
    credentials = { csrfToken, serverAddress };
    try {
        // Initialize RPC client
        initializeRpcClient();
    }
    catch (error) {
        console.error('Failed to initialize RPC client:', error);
        // Reset initialization state if failed
        isInitialized = false;
        rpcClient = null;
    }
};
// Global function for Playwright to call initializeRpcClient
const initializeRpcClientFunc = () => {
    if (credentials) {
        initializeRpcClient();
    }
    else {
        console.error('Cannot initialize - no credentials set');
    }
};
self.setCredentials = setCredentialsFunc;
self.initializeRpcClient = initializeRpcClientFunc;
// Main message handler (called by content script and extension pages)
chrome.runtime.onMessage.addListener((request, sender, sendResponse) => {
    // Wait for initialization, then handle the message
    waitForInitialization()
        .then(() => handleMessageInternal(request, sender))
        .then((result) => {
        sendResponse(result);
    })
        .catch((error) => {
        sendResponse({
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    });
    return true; // Keep message channel open for async response
});
// External message handler (called by web pages using chrome.runtime.sendMessage with extension ID)
chrome.runtime.onMessageExternal.addListener((request, sender, sendResponse) => {
    // Wait for initialization then handle message
    waitForInitialization()
        .then(() => handleMessageInternal(request, sender))
        .then((result) => sendResponse(result))
        .catch((error) => {
        sendResponse({
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    });
    return true; // Keep message channel open for async response
});
function sendSignalToTab(tabId, signal) {
    if (tabId == null) {
        console.warn('Cannot send signal: tabId is null/undefined');
        return;
    }
    chrome.tabs.sendMessage(tabId, signal).catch((error) => {
        console.error(`Failed to send signal to tab:`, error);
    });
}
// Internal message handler
async function handleMessageInternal(request, sender) {
    // Handle different message types using type guards
    // Handle injection script messages (proxyRpcCall)
    if (isInjectionScriptMessage(request)) {
        // Check if RPC client is initialized
        if (!rpcClient) {
            return { success: false, error: 'RPC client not initialized' };
        }
        try {
            const result = await handleRpcProxy(request.method, request.data);
            // Send success response back to the injection script
            sendSignalToTab(sender.tab?.id, {
                type: 'RPC_RESULT',
                pageId: request.pageId,
                payload: {
                    requestId: request.requestId,
                    rpcMethod: request.method,
                    result: result,
                },
            });
            return { success: true };
        }
        catch (error) {
            console.error('RPC proxy error:', error);
            // Send error response back to the injection script
            sendSignalToTab(sender.tab?.id, {
                type: 'RPC_ERROR',
                pageId: request.pageId,
                payload: {
                    requestId: request.requestId,
                    rpcMethod: request.method,
                    error: error instanceof Error ? error.message : 'Unknown error',
                },
            });
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    // Handle utility messages
    if (isUtilityMessage(request)) {
        if (request.action === 'getCurrentTabId') {
            return { tabId: sender.tab?.id || null };
        }
        if (request.action === 'serviceWorkerWakeUp') {
            // Service worker is already awake if we're processing this message
            return { success: true, timestamp: Date.now() };
        }
        if (request.action === 'getMimeType') {
            const tabId = sender.tab?.id;
            if (tabId !== undefined) {
                const mimeType = tabToMimeType.get(tabId);
                return { mimeType: mimeType || null };
            }
            return { mimeType: null };
        }
    }
    // Handle page registration messages
    if (isPageRegistrationMessage(request)) {
        try {
            await handlePageRegistration(request.data);
            return { success: true };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    // Handle CSP violation messages
    if (isCspViolationMessage(request)) {
        try {
            await handleRecordBrowserEvent({
                pageId: request.data.pageId,
                url: request.data.url,
                eventType: 'BROWSER_CSP_VIOLATION',
            });
            return { success: true };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    // Handle script injection failure messages
    if (isScriptInjectionFailedMessage(request)) {
        try {
            handleRecordBrowserEvent({
                pageId: request.data.pageId,
                url: request.data.url,
                eventType: 'BROWSER_SCRIPT_INJECTION_FAILED',
            });
            return { success: true };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    // Handle extension signals from Go backend
    if (isExtensionSignal(request)) {
        return await handleExtensionSignal(request);
    }
    return { success: false, error: 'Unknown message type' };
}
// Handle RPC proxy calls from injected parent script
async function handleRpcProxy(method, data) {
    if (!rpcClient) {
        throw new Error('RPC client not initialized');
    }
    if (!data.protoRequest) {
        throw new Error('No protobuf request data provided');
    }
    // Convert the received data back to Uint8Array (Chrome messaging converts Uint8Array to Array)
    const binaryData = data.protoRequest instanceof Uint8Array ? data.protoRequest : new Uint8Array(data.protoRequest);
    // Route different RPC methods to appropriate client methods
    switch (method) {
        case 'registerPage': {
            const request = RegisterPageRequest.fromBinary(binaryData);
            return await rpcClient.registerPage(request);
        }
        case 'captureAllText': {
            const request = CaptureAllTextRequest.fromBinary(binaryData);
            return await rpcClient.captureAllText(request);
        }
        case 'captureScreenshot': {
            const request = CaptureScreenshotRequest.fromBinary(binaryData);
            return await rpcClient.captureScreenshot(request);
        }
        case 'captureElementScreenshot': {
            const request = CaptureElementScreenshotRequest.fromBinary(binaryData);
            return await rpcClient.captureElementScreenshot(request);
        }
        case 'captureDOMElement': {
            const request = CaptureDOMElementRequest.fromBinary(binaryData);
            return await rpcClient.captureDOMElement(request);
        }
        case 'captureConsoleLog': {
            const request = CaptureConsoleOutputRequest.fromBinary(binaryData);
            return await rpcClient.captureConsoleOutput(request);
        }
        case 'captureElementInnerText': {
            const request = CaptureElementInnerTextRequest.fromBinary(binaryData);
            return await rpcClient.captureElementInnerText(request);
        }
        case 'captureCode': {
            const request = CaptureCodeBlockRequest.fromBinary(binaryData);
            return await rpcClient.captureCodeBlock(request);
        }
        case 'captureSelectedText': {
            const request = CaptureSelectedTextRequest.fromBinary(binaryData);
            return await rpcClient.captureSelectedText(request);
        }
        case 'captureLogBlock': {
            const request = CaptureLogBlockRequest.fromBinary(binaryData);
            return await rpcClient.captureLogBlock(request);
        }
        case 'captureClickEvent': {
            const request = CaptureClickEventRequest.fromBinary(binaryData);
            return await rpcClient.captureClickEvent(request);
        }
        case 'captureScrollEvent': {
            const request = CaptureScrollEventRequest.fromBinary(binaryData);
            return await rpcClient.captureScrollEvent(request);
        }
        default:
            throw new Error(`Unsupported method: ${method}`);
    }
}
// Handle extension signals from Go backend (Phase 2: New Signal Path)
async function handleExtensionSignal(signal) {
    // Forward signal to all tabs that might be interested
    // This implements the signal forwarding: Go Backend → Extension → Injection Script
    try {
        const tabs = await chrome.tabs.query({});
        const forwardPromises = tabs.map((tab) => {
            if (tab.id) {
                return chrome.tabs.sendMessage(tab.id, signal).catch(() => {
                    // Don't log errors for tabs that don't have content scripts
                    // This is expected behavior - silently ignore
                });
            }
            return Promise.resolve();
        });
        await Promise.all(forwardPromises);
        return { success: true };
    }
    catch (error) {
        console.error('Failed to forward extension signal:', error);
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
}
// Handle page registration using Connect-RPC
async function handlePageRegistration(registrationData) {
    if (!rpcClient) {
        throw new Error('RPC client not initialized');
    }
    const { pageId, url, timestamp } = registrationData;
    // Create timestamp using protobuf-es utility
    const timestampMs = typeof timestamp === 'number' ? timestamp : Date.now();
    const timestampProto = Timestamp.fromDate(new Date(timestampMs));
    const request = new RegisterPageRequest({
        pageId: pageId,
        url: url,
        timestamp: timestampProto,
    });
    try {
        await rpcClient.registerPage(request);
    }
    catch (error) {
        console.error('Page registration failed:', error);
        throw error;
    }
}
// Handle recording of browser events using Connect-RPC
async function handleRecordBrowserEvent(eventData) {
    if (!rpcClient) {
        throw new Error('RPC client not initialized');
    }
    const request = new RecordBrowserAnalyticsRequest({
        pageId: eventData.pageId,
        url: eventData.url,
        eventType: eventData.eventType,
    });
    try {
        await rpcClient.recordBrowserAnalytics(request);
    }
    catch (error) {
        console.error('RecordBrowserEvent failed:', error);
        throw error;
    }
}
// =============================================================================
// DEFAULT PAGE REDIRECTION SYSTEM
// =============================================================================
// Simple tab redirection to tutorial page
async function redirectToTutorial(tabId) {
    if (!tabId)
        return;
    try {
        await chrome.tabs.update(tabId, { url: 'https://browser.windsurf.com' });
    }
    catch (error) {
        // Silently ignore redirect failures
    }
}
// Check if URL should be redirected
function shouldRedirect(url) {
    return (!url ||
        url === 'about:blank' ||
        url === 'chrome://newtab/' ||
        url === 'chrome://new-tab-page/' ||
        url.startsWith('edge://newtab') ||
        url.startsWith('opera://startpage'));
}
// Only redirect initial tab on browser startup or extension install
function redirectInitialTab() {
    chrome.tabs.query({}, (tabs) => {
        // Only redirect if there's exactly one tab (typical browser startup scenario)
        if (tabs.length === 1 && tabs[0] && shouldRedirect(tabs[0].url || '')) {
            redirectToTutorial(tabs[0].id);
        }
    });
}
// Run on any service worker startup (covers re-enable after disable)
redirectInitialTab();
// Run when browser starts (additional coverage)
chrome.runtime.onStartup.addListener(() => {
    redirectInitialTab();
});
// Run on extension install (covers first-time install)
chrome.runtime.onInstalled.addListener((details) => {
    if (details.reason === 'install') {
        redirectInitialTab();
    }
});
// Redirect ALL new tabs to browser.windsurf.com
chrome.tabs.onCreated.addListener((tab) => {
    // New tabs typically start with chrome://new-tab-page/ or about:blank
    if (tab.id && tab.pendingUrl && shouldRedirect(tab.pendingUrl)) {
        redirectToTutorial(tab.id);
    }
    else if (tab.id && tab.url && shouldRedirect(tab.url)) {
        redirectToTutorial(tab.id);
    }
});
// Also catch tabs that navigate to new tab page
chrome.tabs.onUpdated.addListener((tabId, changeInfo) => {
    if (changeInfo.url && shouldRedirect(changeInfo.url)) {
        redirectToTutorial(tabId);
    }
});
// =============================================================================
// SERVICE WORKER WAKE-UP FOR PLAYWRIGHT VISIBILITY
// =============================================================================
/**
 * Simple wake-up to ensure service worker is active and visible to Playwright.
 * Manifest V3 service workers are lazy - they need to be active to show up in
 * context.serviceWorkers(). This ensures Playwright can find the extension.
 */
function wakeUpServiceWorker() {
    // Send a self-message to activate the service worker
    chrome.runtime.sendMessage(chrome.runtime.id, { type: 'PING' }).catch(() => {
        // Expected - self-messaging often produces this error, but it activates the worker
        // This is normal and expected - silently ignore
    });
    // Keep active for 60 seconds after startup to ensure Playwright has time to detect
    const keepAliveInterval = setInterval(() => {
        // Keep-alive ping
    }, 10000); // Every 10 seconds
    setTimeout(() => {
        clearInterval(keepAliveInterval);
    }, 60000); // Stop after 60 seconds
}
// Wake up the service worker immediately when this script loads
// This makes the worker visible to Playwright's serviceWorkers() detection
wakeUpServiceWorker();
// Also wake up on extension install/update
chrome.runtime.onInstalled.addListener(() => {
    wakeUpServiceWorker();
});
